import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Grid,
  Alert,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { auth, db, uploadImage } from "../firebase"; // Asegúrate de importar tus configuraciones de Firebase
import {
  doc,
  getDoc,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import Atras from "./atras";
import { locatePlace } from "../placeLocation";
import GooglePlaceAutocomplete, { toAddressFields } from "./GooglePlaceAutocomplete";
import PlaceAutocomplete, { COUNTRIES, loadStates } from "./PlaceAutocomplete";

const Publish = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    place: "",
    timeToDo: new Date(),
    images: [],
    city: "",
    address: "",
    province: "",
    country: "",
  });
  const [publishSuccess, setPublishSuccess] = useState(false); // Estado para controlar el éxito
  const navigate = useNavigate(); // Hook para redireccionar
  // Set when an owner arrives from a Google hotel on the home map ("¿Es tu alojamiento?").
  // Only Google's Place ID is kept (its terms allow storing it); the owner fills in everything else.
  const [searchParams] = useSearchParams();
  const googlePlaceId = searchParams.get("googlePlaceId");
  const [loading, setLoading] = useState(false);

  // Location is filled top-down: country → province → locality → address, each narrowing the next.
  const selectedCountry = COUNTRIES.find((c) => c.name === formData.country) || null;
  const countryCode = selectedCountry?.code;
  const [states, setStates] = useState({ countryCode: null, list: [] });
  const [cityLocation, setCityLocation] = useState(null); // Only biases address suggestions; not saved.

  useEffect(() => {
    if (!countryCode) return undefined;
    let cancelled = false;
    loadStates(countryCode)
      .then((list) => !cancelled && setStates({ countryCode, list }))
      .catch(() => !cancelled && setStates({ countryCode, list: [] }));
    return () => {
      cancelled = true;
    };
  }, [countryCode]);

  const statesLoading = Boolean(countryCode) && states.countryCode !== countryCode;
  const provinceData = states.list.find((s) => s.name === formData.province);
  const provinceLocation = provinceData && {
    lat: Number(provinceData.lat),
    lng: Number(provinceData.lon),
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const handlePublishProyect = async () => {
    const { uid } = auth.currentUser || {};

    const userDocRef = doc(db, "users", uid);
    const userDocSnapshot = await getDoc(userDocRef);

    if (userDocSnapshot.exists()) {
      const userData = userDocSnapshot.data();
      const userName = userData.displayName;

      const placeRef = await addDoc(collection(db, "places"), {
        name: userName,
        title: formData.title,
        description: formData.description,
        price: formData.price,
        city: formData.city,
        address: formData.address,
        province: formData.province,
        country: formData.country,
        place: formData.place,
        images: formData.images,
        createdAt: serverTimestamp(),
        uid,
        // The link to the Google hotel stays pending until an admin verifies the owner
        // (set claimStatus to "verified" in Firestore); only then is it shown as that hotel.
        ...(googlePlaceId && {
          googlePlaceId,
          claimStatus: "pending",
          claimRequestedAt: serverTimestamp(),
        }),
      });
      // Put it on the home map; runs in the background so publishing isn't delayed.
      locatePlace({ id: placeRef.id, ...formData }).catch((error) =>
        console.error("No se pudo ubicar la publicación:", error)
      );
      // Owners claiming a Google hotel go straight to verifying it.
      if (googlePlaceId) {
        navigate(`/verificar/${placeRef.id}`);
        return;
      }
      setPublishSuccess(true);
    } else {
      console.error("User document does not exist");
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files; // Get all selected files

    if (!files) {
      return;
    }

    setLoading(true);

    try {
      const imageUrls = []; // Array to store URLs of all uploaded images

      for (const file of files) {
        const imageUrl = await uploadImage(file);
        imageUrls.push(imageUrl);
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        images: [...prevFormData.images, ...imageUrls], // Add all new URLs to the array
      }));
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      images: prevFormData.images.filter((_, i) => i !== index),
    }));
  };

  const handleRedirectHome = () => {
    navigate("/home");
  };

  const handlePublishClick = async () => {
    await handlePublishProyect();
  };

  const styles = {
    uploadButton: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "50px",
      backgroundColor: "#f0f0f0",
      border: "2px solid rgba(130, 245, 184, 0.35)",
      marginTop: "20px",
      borderRadius: "8px",
      color: "black",
    },
    progress: {
      position: "absolute",
      color: "#4ade80",
    },
    imagePreview: {
      width: "100px",
      height: "100px",
      objectFit: "cover",
      margin: "5px",
      border: "1px solid #ccc",
      borderRadius: "5px",
    },
    removeButton: {
      display: "block",
      position: "absolute",
      top: "5px",
      right: "5px",
      width: "60px",
      height: "60px",
      backgroundColor: "red",
      color: "white",
      borderRadius: "50%",
      cursor: "pointer",
    },
  };

  return (
    <Container>
      <AppBar position="fixed" sx={{ bgcolor: "black" }}>
        <Toolbar
          sx={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
          }}
        >
          <Atras color="inherit" />
          <Container style={styles.user}>
            <Typography variant="h5" color="white" gutterBottom>
              Publicar Alojamiento
            </Typography>
          </Container>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 14, mb: 20 }}>
        {googlePlaceId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Estás publicando tu alojamiento desde el mapa. Completá los datos con tu información: vamos a
            verificar que seas el dueño antes de mostrarlo como ese alojamiento.
          </Alert>
        )}
        <TextField
          autoFocus
          margin="dense"
          id="title"
          label="Título"
          type="text"
          fullWidth
          name="title"
          value={formData.title}
          onChange={handleInputChange}
        />
        <TextField
          margin="dense"
          id="description"
          label="Describe la propiedad"
          type="text"
          fullWidth
          name="description"
          multiline
          rows={5}
          value={formData.description}
          onChange={handleInputChange}
          className={styles.textField}
        />
        <TextField
          margin="dense"
          id="price"
          label="Precio por un día"
          type="text"
          fullWidth
          name="price"
          value={formData.price}
          onChange={handleInputChange}
        />
        <Typography sx={{ mt: 2 }}>Seleccione un lugar:</Typography>
        <Select
          label="Lugares"
          type="text"
          name="place"
          sx={{ mt: 1.5, marginBottom: 1 }}
          value={formData.place}
          onChange={handleInputChange}
          displayEmpty
          fullWidth
        >
          <MenuItem value="" disabled>
            Selecciona un continente
          </MenuItem>
          <MenuItem value="Asia">Asia</MenuItem>
          <MenuItem value="América del Norte">América del Norte</MenuItem>
          <MenuItem value="América del Sur">América del Sur</MenuItem>
          <MenuItem value="Europa">
            Europa
          </MenuItem>
        </Select>
        <PlaceAutocomplete
          label="País"
          value={selectedCountry}
          options={COUNTRIES}
          getOptionLabel={(c) => c.name}
          onChange={(c) => {
            setCityLocation(null);
            setFormData((prev) => ({ ...prev, country: c?.name || "", province: "", city: "", address: "" }));
          }}
        />
        <PlaceAutocomplete
          label="Provincia / Estado"
          value={provinceData || formData.province || null}
          options={states.list}
          getOptionLabel={(s) => s.name}
          // Countries without provinces in the dataset get a free-text field.
          freeSolo={!statesLoading && states.list.length === 0}
          loading={statesLoading}
          disabled={!selectedCountry}
          onChange={(state) => {
            setCityLocation(null);
            setFormData((prev) => ({
              ...prev,
              province: typeof state === "string" ? state : state?.name || "",
              city: "",
              address: "",
            }));
          }}
        />
        <GooglePlaceAutocomplete
          label="Localidad"
          value={formData.city}
          types={["(cities)"]}
          regionCode={countryCode}
          bias={provinceLocation}
          fields={["location"]}
          disabled={!formData.province}
          onChange={(city) => setFormData((prev) => ({ ...prev, city }))}
          onSelect={(place, option) => {
            setCityLocation(place.location?.toJSON() || null);
            setFormData((prev) => ({ ...prev, city: option.main, address: "" }));
            return option.main;
          }}
        />
        <GooglePlaceAutocomplete
          label="Dirección"
          value={formData.address}
          regionCode={countryCode}
          bias={cityLocation || provinceLocation}
          context={[formData.city, formData.province].filter(Boolean).join(", ")}
          disabled={!formData.city}
          onChange={(address) => setFormData((prev) => ({ ...prev, address }))}
          onSelect={(place, option) => {
            const fields = toAddressFields(place.addressComponents || [], option.main);
            setFormData((prev) => ({ ...prev, address: fields.address }));
            return fields.address;
          }}
        />
        <label htmlFor="upload-button" style={{ width: "100%" }}>
          <Button
            variant="contained"
            fullWidth
            component="div" // Cambiado a "div" para evitar el enfoque del botón
            sx={styles.uploadButton}
            disabled={loading}
          >
            <AddPhotoAlternateIcon sx={{ color: "black" }} />
            Subir Imagen del producto
            {loading && (
              <CircularProgress size={40} thickness={4} sx={styles.progress} />
            )}
          </Button>
        </label>
        <input
          type="file"
          accept="image/*"
          onClick={(event) => (event.target.value = null)}
          onChange={handleImageUpload}
          style={{ display: "none" }}
          id="upload-button"
          multiple // Add the multiple attribute
        />

        {/* Vista previa de las imágenes */}
        <Grid container sx={{ mt: 2 }}>
          {formData.images.map((imageUrl, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  border: "1.5px solid black",
                  borderRadius: "10px",
                }}
              >
                <img
                  src={imageUrl}
                  alt="Imagen de la propiedad"
                  style={styles.imagePreview}
                />
                <Button
                  variant="contained"
                  sx={styles.removeButton}
                  onClick={() => handleRemoveImage(index)}
                >
                  X
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>

        {publishSuccess && (
          <Box sx={{ textAlign: "center" }}>
            <Typography>El anuncio se ha publicado correctamente.</Typography>
            <Button onClick={handleRedirectHome} color="primary">
              Ir a Inicio
            </Button>
          </Box>
        )}
        {!publishSuccess && (
          <Button
            variant="contained"
            sx={{ mt: 3 }}
            fullWidth
            onClick={handlePublishClick} // Use handlePublishClick here
            color="primary"
          >
            Publicar
          </Button>
        )}
      </Container>
    </Container>
  );
};

export default Publish;
